import { useEffect, useRef, useState } from "react";
import { MiniCard, TALL_CARD_ASPECT } from "@/components/cards/card-primitives";
import { TcgDesignCard } from "@/components/cards/designs/tcg-design-card";
import type { InventoryPokemonsOutput } from "types/web-app";

type InventoryPokemon = InventoryPokemonsOutput["player_pokemons"][0];

/** Inventory grid only — shared card stays 170px elsewhere. */
const INVENTORY_CARD_WIDTH = 140;
/** DesignMiniCard content height / 170 */
const MINI_CARD_ASPECT = 0.86;

export const CardPokeItem = ({
  pokemon,
  selected,
  isInTeam,
  mini = false,
  onClick,
  onLongPress,
}: {
  pokemon: InventoryPokemon;
  selected: boolean;
  isInTeam?: boolean;
  mini?: boolean;
  onClick: () => void;
  onLongPress?: () => void;
}) => {
  const [isPressing, setIsPressing] = useState(false);
  const timerRef = useRef<number | null>(null);
  const longPressFiredRef = useRef(false);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => () => clearTimer(), []);

  const startPress = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!onLongPress) return;
    setIsPressing(true);
    longPressFiredRef.current = false;
    startPosRef.current = { x: e.clientX, y: e.clientY };
    timerRef.current = window.setTimeout(() => {
      longPressFiredRef.current = true;
      setIsPressing(false);
      onLongPress();
    }, 500);
  };

  const endPress = () => {
    setIsPressing(false);
    clearTimer();
  };

  const cancelIfMovedTooMuch = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isPressing || !startPosRef.current) return;
    const dx = Math.abs(e.clientX - startPosRef.current.x);
    const dy = Math.abs(e.clientY - startPosRef.current.y);
    if (dx > 10 || dy > 10) endPress();
  };

  const handleClick = () => {
    if (longPressFiredRef.current) return;
    onClick();
  };

  return (
    <div
      className="relative w-fit mx-auto cursor-pointer select-none"
      style={{ opacity: isInTeam ? 0.5 : 1 }}
      onPointerDown={startPress}
      onPointerUp={endPress}
      onPointerCancel={endPress}
      onPointerLeave={endPress}
      onPointerMove={cancelIfMovedTooMuch}
      onClick={handleClick}
      onContextMenu={(e) => e.preventDefault()}
    >
      <MiniCard
        width={INVENTORY_CARD_WIDTH}
        aspect={mini ? MINI_CARD_ASPECT : TALL_CARD_ASPECT}
      >
        <TcgDesignCard
          pokeName={pokemon.base_pokemon_name}
          level={pokemon.level}
          isShiny={pokemon.is_shiny}
          isFullArt={pokemon.is_full_art}
          activeSkills={pokemon.active_skills}
          talents={pokemon.talents.map(String)}
          mini={mini}
        />
      </MiniCard>
      {selected && (
        <div className="absolute inset-0 rounded-xl ring-4 ring-pk-orange-4 ring-offset-2 pointer-events-none" />
      )}
      {isInTeam && (
        <div className="absolute top-2 right-2 z-50 bg-pk-orange-4 text-white text-[8px] font-bold px-1 rounded-sm shadow-cartoon-sm">
          TEAM
        </div>
      )}
    </div>
  );
};
